import { Terminal, RefreshCw, Search, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UseCasesProps {
  data: {
    title: string;
    categories: Array<{
      title: string;
      description: string;
      detail: string;
    }>;
  };
  className?: string;
}

const iconMap = {
  'Rotate Credentials': RefreshCw,
  'Patch & Reboot': Terminal,
  'Detect Drift': Search,
  'Activate Devices': Smartphone,
};

/**
 * Use Cases Section Component
 * Shows real command-level use cases
 */
export function UseCases({ data, className }: UseCasesProps) {
  return (
    <section id="use-cases" className={cn("py-20 bg-background", className)}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold">
            {data.title}
          </h2>
        </div>

        {/* Use Cases Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {data.categories.map((useCase, index) => {
            const IconComponent = iconMap[useCase.title as keyof typeof iconMap] || Terminal;
            
            return (
              <div key={index} className="gradient-card rounded-2xl p-8 shadow-card hover:shadow-lg transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                  
                  <div className="flex-1 space-y-3">
                    <h3 className="text-xl font-semibold">{useCase.title}</h3>
                    
                    <div className="bg-muted/50 rounded-lg p-4 border-l-4 border-primary">
                      <p className="text-sm font-mono text-foreground">
                        {useCase.description}
                      </p>
                    </div>
                    
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {useCase.detail}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
